import { useState, useEffect, useCallback } from 'react';
import { getResearches, checkQuota } from '../api/userApi';

/**
 * Custom hook for managing research data and quotas.
 * Centralizes fetching logic to reduce bloating in page components.
 */
export const useResearch = (uid) => {
    const [researches, setResearches] = useState([]);
    const [quotas, setQuotas] = useState({ report: 0, chat: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!uid) return;
        setIsLoading(true);
        setError(null);
        try {
            const [researchesRes, reportQuotaRes, chatQuotaRes] = await Promise.all([
                getResearches(uid),
                checkQuota(uid, "report_generation"),
                checkQuota(uid, "chat_message")
            ]);

            if (researchesRes.status) {
                setResearches(researchesRes.data);
            } else {
                throw new Error("Failed to fetch researches");
            }

            setQuotas({
                report: reportQuotaRes.status ? reportQuotaRes.remaining : 0,
                chat: chatQuotaRes.status ? chatQuotaRes.remaining : 0
            });
        } catch (err) {
            console.error("Error fetching research data:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [uid]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const refreshQuotas = async () => {
        if (!uid) return;
        try {
            const [reportQuotaRes, chatQuotaRes] = await Promise.all([
                checkQuota(uid, "report_generation"),
                checkQuota(uid, "chat_message")
            ]);

            setQuotas({
                report: reportQuotaRes.status ? reportQuotaRes.remaining : 0,
                chat: chatQuotaRes.status ? chatQuotaRes.remaining : 0
            });
        } catch (error) {
            console.error("Error refreshing quotas:", error);
        }
    };

    return {
        researches,
        setResearches,
        quotas,
        setQuotas,
        isLoading,
        error,
        fetchData,
        refreshQuotas
    };
};
